import React, { createContext, useContext, useLayoutEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

type Theme = 'light' | 'dark';

interface ThemeContextType {
  theme: Theme;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

const MARKETING_THEME_LOCK_PREFIXES = ['/solutions', '/pricing', '/help', '/auth'] as const;

export function isMarketingThemeLockPath(pathname: string): boolean {
  // Exact `/` only — never startsWith('/') or every app route would lock dark.
  if (pathname === '/') return true;
  return MARKETING_THEME_LOCK_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { pathname } = useLocation();
  const lockDark = isMarketingThemeLockPath(pathname);
  const [theme, setTheme] = useState<Theme>(() => {
    // Получаем сохраненную тему из localStorage
    const savedTheme = localStorage.getItem('theme') as Theme | null;
    return savedTheme || 'dark'; // По умолчанию темная тема
  });

  useLayoutEffect(() => {
    const applied = lockDark ? 'dark' : theme;
    document.documentElement.setAttribute('data-theme', applied);
    if (!lockDark) {
      localStorage.setItem('theme', theme);
    }
  }, [theme, lockDark]);

  const toggleTheme = () => {
    setTheme((prevTheme) => (prevTheme === 'dark' ? 'light' : 'dark'));
  };

  return <ThemeContext.Provider value={{ theme, toggleTheme }}>{children}</ThemeContext.Provider>;
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
